import {SVG} from '../../../../../constants/assetLists';
import {useMemo} from 'react';
import AppTable, {ColumnDef} from '../../../../../components/common/AppTable';
import {useUsersContext} from '../../../../../context/users/UsersContextProvider';
import {UserItem} from '../users';
import moment from 'moment';
import {ICONS} from '../../../data';
import {formatNumber, getRoleTypeName} from '../../../../../utils';

function UsersTable() {
  const {allUsers, selectedUsers, setSelectedUsers, page, setPage} =
    useUsersContext();

  const handleOpenMail = (email: string) => {
    window.open(`mailto:${email}`, '_blank');
  };

  const columns = useMemo<ColumnDef<UserItem>[]>(
    () => [
      {
        title: 'שם משתמש',
        field: 'firstName',
        accessor: r => r?.firstName + ' ' + r?.lastName,
        sortable: true,
      },
      {
        title: 'אימייל',
        field: 'email',
        accessor: 'email',
        sortable: true,
        render: r => (
          <span
            onClick={() => handleOpenMail(r?.email)}
            className={`${
              r.status === 'active' ? 'text-linkBlue underline' : 'text-gray'
            } cursor-pointer`}>
            {r?.email || '-'}
          </span>
        ),
      },
      {
        title: 'טלפון',
        field: 'phone',
        accessor: 'phone',
        sortable: true,
        width: 250,
        render: r => (
          <span dir="ltr">{r.phone ? formatNumber(r.phone) : '-'}</span>
        ),
      },
      {
        title: 'תפקיד',
        field: 'roles',
        accessor: r => getRoleTypeName(r?.roles[0]?.roleType) || '-',
        sortable: true,
        width: 150,
        render: r => {
          const icon =
            ICONS?.find(icon => icon.id === r?.roles[0]?.icon)?.icon || '';
          return (
            <div className="flex items-center gap-2">
              {icon && (
                <img
                  src={icon}
                  alt="icon"
                  className="w-[20px] h-[20px] object-contain"
                />
              )}
              <span className="break-all">
                {getRoleTypeName(r?.roles[0]?.roleType) || '-'}
              </span>
            </div>
          );
        },
      },
      {
        title: 'סטאטוס חשבון',
        field: 'status',
        accessor: 'status',
        sortable: false,
        render: r => (
          <div className="flex items-center gap-2">
            {r.status === 'active' ? (
              <div className="w-[8px] h-[8px] rounded-full bg-green" />
            ) : (
              <img
                src={SVG.closeGrayIcon}
                alt="icon"
                className="w-[10px] h-[10px] object-contain"
              />
            )}
            <span>{r.status === 'active' ? 'פעיל' : 'לא פעיל'}</span>
          </div>
        ),
      },
      {
        title: 'משתמש באפליקציה',
        field: 'isAppUser',
        accessor: 'isAppUser',
        sortable: false,
        width: 160,
        render: r => (
          <div className="flex justify-center items-center w-full">
            {r.isAppUser ? (
              <img src={SVG.tickGreenIcon} className="w-[25px] h-[25px]" />
            ) : (
              <span style={{color: r.isAppUser ? '#038D92' : '#707070'}}>
                —
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'בית עסק',
        field: 'clientId',
        accessor: r => {
          const clientNames = r?.clientId
            ?.map(client => client?.name)
            .join(', ');
          const branchNames = r?.branchId
            ?.map(branch => branch?.name)
            .join(', ');
          const subbranchNames = r?.subBranchId
            ?.map(subbranch => subbranch?.name)
            .join(', ');  
          const combinedNames = [clientNames, branchNames, subbranchNames]
            .filter(Boolean)
            .join(', ');
          return combinedNames || '-';
        },
        sortable: true,
      },
      {
        title: 'תאריך יצירה',
        field: 'createdAt',
        accessor: r => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
        sortable: true,
      },
      {
        title: 'משוייך לקבוצה',
        field: 'associatedGroups',
        accessor: r =>
          r?.associatedGroups?.map(group => group?.groupName)?.join(', '),
        sortable: true,
      },
    ],
    [],
  );

  return (
    <AppTable<UserItem>
      data={allUsers?.data || []}
      columns={columns}
      rowKey={r => r?._id}
      selectable
      selectedKeys={selectedUsers}
      onSelectionChange={setSelectedUsers}
      rowDisabled={r => r.status !== 'active'}
      showPagination={(allUsers?.pagination?.total || 0) > 50}
      totalRecords={allUsers?.pagination?.total || 0}
      currentPage={page - 1}
      onPageChange={(page: number) => setPage(page + 1)}
      limit={50}
    />
  );
}

export default UsersTable;
